// Aplicar ediciones (del vendedor o propuestas por la IA) al borrador. Lógica compartida: el servidor la usa para
// guardar y la interfaz para enseñar la vista previa del cambio. Reglas:
//  - Solo se tocan rutas EDITABLES (resultado, avisos, archivos y revision_ia nunca: los pone el sistema).
//  - "antes" debe coincidir con lo guardado; si no: EDICION_DESACTUALIZADA (no se pisa el cambio de otra persona).
//  - El contenido final se valida COMPLETO con ContenidoCotizacion; si no pasa: EDICION_INVALIDA con los motivos.
//  - No muta la entrada: devuelve una copia.
import { ContenidoCotizacion } from './dominio/cotizacion.ts';
import type { EdicionCampo } from './dominio/edicion.ts';
import { ErrorCotizador } from './errores.ts';
import { MOTIVO_EDITADO_A_MANO, periodoConConsumoEditado, periodosDeAntes } from './ajuste-consumos.ts';
import { MOTIVO_SUGERIR_IA } from './sugerir-ia.ts';
import type { PeriodoConsumo } from './dominio/recibo.ts';

const SEG = '[^/]+';
const IDX = '(?:\\d+|-)';
/** Rutas donde se puede REEMPLAZAR un valor. */
const REEMPLAZABLES: readonly RegExp[] = [
  new RegExp(`^/cuestionario(/${SEG})*$`),
  new RegExp(`^/lecturas/[AB](/${SEG})*$`),
  new RegExp(`^/parametros(/${SEG})*$`),
  /^\/costos_adicionales\/\d+\/(concepto|cantidad|precio_cliente_sin_iva|costo_solarix_sin_iva|aplica_descuento_contado|reportado_por|nota)$/,
  // Ampliación de baterías (27-oct-2026): lo que captura el vendedor. /calculo_piezas NO: lo calcula el servidor.
  new RegExp(`^/piezas(/${SEG})*$`),
];
/** Listas donde se puede AGREGAR o QUITAR un elemento. */
const LISTAS: readonly RegExp[] = [
  new RegExp(`^/lecturas/[AB]/(historial|precios_energia|campos_dudosos|archivo_ids)/${IDX}$`),
  new RegExp(`^/parametros/(ajustes|opciones_activas)/${IDX}$`),
  new RegExp(`^/parametros/formas_de_pago/msi/${IDX}$`),
  new RegExp(`^/cuestionario/estimacion/cargas/${IDX}$`),
  // Contrato v2 · baterías: los equipos que se respaldan.
  new RegExp(`^/cuestionario/respaldo/cargas/${IDX}$`),
];
/** ADENDA A0: /parametros/finsus (cotizaciones de FINSUS copiadas a mano) es LEGADO. El motor lo ignora y la
 *  mensualidad de referencia sale sola de la fórmula; ni la interfaz ni la IA lo ofrecen ya. No se puede agregar ni
 *  cambiar; solo QUITAR lo que traiga un borrador viejo (para que deje de salir su nota informativa). Para poner o
 *  quitar FINSUS se usa /parametros/formas_de_pago/finsus. */
const FINSUS_LEGADO = /^\/parametros\/finsus(\/|$)/;
const FINSUS_LEGADO_QUITAR = /^\/parametros\/finsus\/\d+$/;

/** Etapa 4: quién autorizó la tabla leída la pone SOLO el servidor (POST …/lecturas/:medidor/autorizar). */
const AUTORIZADA = /^\/lecturas\/[AB]\/autorizada(\/|$)/;

export function rutaEditable(op: EdicionCampo['op'], ruta: string): boolean {
  if (FINSUS_LEGADO.test(ruta)) return op === 'quitar' && FINSUS_LEGADO_QUITAR.test(ruta);
  if (AUTORIZADA.test(ruta)) return false;
  if (op === 'reemplazar') return REEMPLAZABLES.some((r) => r.test(ruta));
  return LISTAS.some((r) => r.test(ruta));
}

function partes(ruta: string): string[] {
  if (!ruta.startsWith('/')) throw new ErrorCotizador('EDICION_INVALIDA', `Ruta sin "/" inicial: ${ruta}`);
  return ruta.slice(1).split('/').map((p) => p.replace(/~1/g, '/').replace(/~0/g, '~'));
}

/** Valor en una ruta JSON pointer (undefined si no existe). */
export function leerRuta(obj: unknown, ruta: string): unknown {
  let cur: unknown = obj;
  for (const p of partes(ruta)) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = Array.isArray(cur) ? (p === '-' ? undefined : cur[Number(p)]) : (cur as Record<string, unknown>)[p];
  }
  return cur;
}

export function iguales(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a === 'number' && typeof b === 'number') return Number.isNaN(a) && Number.isNaN(b);
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) return a.length === b.length && a.every((x, i) => iguales(x, b[i]));
  const ka = Object.keys(a as object).filter((k) => (a as Record<string, unknown>)[k] !== undefined);
  const kb = Object.keys(b as object).filter((k) => (b as Record<string, unknown>)[k] !== undefined);
  return ka.length === kb.length && ka.every((k) => iguales((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

/** Punto q (Diego, 13-oct, 19:05): `paneles_del_precio` de un ajuste lo escribe el SERVIDOR al recalcular (el tamaño con que se
 *  escribió el precio a mano). No es algo que el usuario "vio": quien edita con el ajuste tal como lo mandó (sin ese campo)
 *  no debe recibir «cambió desde que lo viste». Solo para comparar «antes»; nunca se quita del contenido. */
function sinMetaDelServidor(x: unknown): unknown {
  if (Array.isArray(x)) return x.map(sinMetaDelServidor);
  if (x === null || typeof x !== 'object') return x;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(x)) if (k !== 'paneles_del_precio') out[k] = sinMetaDelServidor(v);
  return out;
}
const igualesParaEditar = (a: unknown, b: unknown) => iguales(sinMetaDelServidor(a), sinMetaDelServidor(b));

function aplicarUna(raiz: Record<string, unknown>, e: EdicionCampo): void {
  if (!rutaEditable(e.op, e.ruta)) throw new ErrorCotizador('EDICION_INVALIDA', `No se puede ${e.op} en ${e.ruta}: no es un campo editable.`);
  const ps = partes(e.ruta);
  const ultima = ps.pop()!;
  const padreReal = ps.length ? leerRuta(raiz, '/' + ps.map((p) => p.replace(/~/g, '~0').replace(/\//g, '~1')).join('/')) : raiz;
  if (padreReal === null || typeof padreReal !== 'object')
    throw new ErrorCotizador('EDICION_INVALIDA', `La ruta ${e.ruta} no existe (su contenedor no está).`);

  if (Array.isArray(padreReal)) {
    const n = padreReal.length;
    if (e.op === 'agregar') {
      if (e.antes !== null && e.antes !== undefined) throw new ErrorCotizador('EDICION_INVALIDA', `Al agregar en ${e.ruta}, "antes" debe ser null.`);
      const i = ultima === '-' ? n : Number(ultima);
      if (!Number.isInteger(i) || i < 0 || i > n) throw new ErrorCotizador('EDICION_INVALIDA', `Posición fuera de la lista en ${e.ruta}.`);
      padreReal.splice(i, 0, structuredClone(e.despues));
      return;
    }
    const i = Number(ultima);
    if (!Number.isInteger(i) || i < 0 || i >= n) throw new ErrorCotizador('EDICION_INVALIDA', `Posición fuera de la lista en ${e.ruta}.`);
    if (!igualesParaEditar(padreReal[i], e.antes))
      throw new ErrorCotizador('EDICION_DESACTUALIZADA', `El valor de ${e.ruta} cambió desde que lo viste: recarga la cotización.`);
    if (e.op === 'quitar') padreReal.splice(i, 1);
    else padreReal[i] = structuredClone(e.despues);
    return;
  }

  if (e.op !== 'reemplazar') throw new ErrorCotizador('EDICION_INVALIDA', `Solo se agrega o quita en listas (${e.ruta}).`);
  const obj = padreReal as Record<string, unknown>;
  // Un campo OPCIONAL que todavía no existe (p. ej. /cuestionario/respaldo en un borrador de antes del contrato v2) se
  // puede poner si quien edita vio "nada" (antes null). Un campo inventado no pasa: la validación final del contenido
  // es strict y truena con EDICION_INVALIDA.
  const existe = Object.hasOwn(obj, ultima);
  if (!existe && e.antes !== null && e.antes !== undefined) throw new ErrorCotizador('EDICION_INVALIDA', `El campo ${e.ruta} no existe.`);
  if (existe && !igualesParaEditar(obj[ultima], e.antes))
    throw new ErrorCotizador('EDICION_DESACTUALIZADA', `El valor de ${e.ruta} cambió desde que lo viste: recarga la cotización.`);
  obj[ultima] = structuredClone(e.despues);
}

const OPCIONES_COBERTURA = ['arranque', 'exacta', 'holgada'] as const;
/** z16/z23 (20-oct): quien escribe la cobertura de una opción la deja ESCRITA aunque sea igual a la de omisión (el motor la
 *  respeta y no la cambia por el «Mejor retorno» de casa 1F). Al reemplazar todo el objeto, cuentan las que cambiaron. */
function marcarCoberturasEscritas(copia: Record<string, unknown>, antes: ContenidoCotizacion, ediciones: readonly EdicionCampo[]): void {
  const escritas = new Set<string>();
  for (const e of ediciones) {
    if (e.op !== 'reemplazar') continue;
    const m = /^\/parametros\/coberturas_objetivo(?:\/(arranque|exacta|holgada))?$/.exec(e.ruta);
    if (!m) continue;
    if (m[1]) escritas.add(m[1]);
    else for (const id of OPCIONES_COBERTURA) if (!iguales((e.despues as Record<string, unknown> | null)?.[id], antes.parametros.coberturas_objetivo[id])) escritas.add(id);
  }
  const par = copia.parametros;
  if (!escritas.size || par === null || typeof par !== 'object') return;
  const prm = par as Record<string, unknown>;
  const previas = Array.isArray(prm.coberturas_escritas) ? (prm.coberturas_escritas as unknown[]) : [];
  prm.coberturas_escritas = OPCIONES_COBERTURA.filter((id) => previas.includes(id) || escritas.has(id));
}

/** z16/z23 (20-oct, REGLA DE FONDO tras tres verificaciones con el mismo síntoma: el vendedor veía la opción con N paneles
 *  elegidos por retorno, escribía solo el precio, y el PDF vendía el Arranque de siempre con MÁS paneles a ese precio).
 *  Lo que el vendedor ve es lo que se vende: si escribe el PRECIO (nuevo o distinto) sin paneles ni % de ahorro en una opción
 *  cuyos paneles salieron de la búsqueda por retorno (`paneles_por_retorno` del cálculo guardado, lleve o no el nombre «Mejor
 *  retorno»), el ajuste guarda ESOS paneles con la marca `paneles_de_retorno` (no los escribió él). La marca sigue mientras
 *  los paneles sean los mismos (la forma los muestra en su campo y los manda de vuelta); con otros paneles, o con % de ahorro,
 *  son del vendedor y se quita. Y si el precio vuelve a la tabla («Precio de tabla»), esos paneles se van con el ajuste: el
 *  cotizador vuelve a buscar. El servidor la pone y la quita: nadie de fuera la puede fingir para cambiarle el nombre a sus
 *  paneles (y el motor, además, mide el retorno con los precios finales antes de llamarla «Mejor retorno»). */
function fijarPanelesDelMejorRetorno(copia: Record<string, unknown>, antes: ContenidoCotizacion): void {
  const porRetorno = antes.resultado?.opciones.filter((o) => o.paneles_por_retorno === true) ?? [];
  const par = copia.parametros;
  if (par === null || typeof par !== 'object') return;
  const prm = par as Record<string, unknown>;
  const lista = prm.ajustes;
  if (!Array.isArray(lista)) return;
  const nueva: unknown[] = [];
  for (const x of lista) {
    const a = x as Record<string, unknown> | null;
    if (a === null || typeof a !== 'object') {
      nueva.push(x);
      continue;
    }
    const previo = antes.parametros.ajustes.find((y) => y.opcion === a.opcion);
    const deRetornoAntes = previo?.paneles_de_retorno === true;
    const o = porRetorno.find((y) => y.id === a.opcion);
    const sinAhorro = a.ahorro_pct == null;
    const precioNuevo = (previo?.precio_lista_sin_iva ?? null) !== (a.precio_lista_sin_iva ?? null);
    let b: Record<string, unknown> = { ...a };
    delete b.paneles_de_retorno;
    if (o && a.paneles == null && sinAhorro && a.precio_lista_sin_iva != null && (precioNuevo || deRetornoAntes)) b = { ...b, paneles: o.paneles, paneles_de_retorno: true };
    else if (deRetornoAntes && a.paneles != null && a.paneles === previo!.paneles && sinAhorro) b = { ...b, paneles_de_retorno: true };
    // R4: sin precio a mano, los paneles del retorno no son de nadie: se van con el ajuste y el cotizador vuelve a buscar
    if (b.paneles_de_retorno === true && b.precio_lista_sin_iva == null) continue;
    nueva.push(b);
  }
  prm.ajustes = nueva;
}

/** z25b (Diego, 20-oct): si una edición cambia los kWh de un periodo del historial (la celda de la tabla de Revisión,
 *  el periodo completo o el historial completo al agregar o quitar renglones), lo que cobró el recibo por el consumo viejo
 *  ya no vale: ver periodoConConsumoEditado (importe, precio medio y cargos en null para que el motor estime ese pago con
 *  las cuotas de CFE; su importe se respeta si lo escribió en el mismo cambio). Vive AQUÍ para que no dependa de quién
 *  llama (pantalla, servidor, IA). Con agregar/quitar renglones, el periodo de antes se reconoce con periodosDeAntes (fechas,
 *  corte, etiqueta, importe y kWh). hoy85 (verificador del vendedor): reemplazar la lectura ENTERA sobre una tabla que ya tenía
 *  periodos (la API directa, o la IA que propone «la lectura completa corregida») TAMBIÉN cuenta: antes se podían cambiar o
 *  intercambiar los kWh de meses del recibo (1,933 → 3,000) y el importe impreso se quedaba pegado a los kWh nuevos, sin
 *  `ajuste_vendedor` y con origen «del recibo». La primera lectura de un medidor (sin periodos antes) no tiene nada que reconocer. */
function marcarConsumosEditados(copia: Record<string, unknown>, antes: ContenidoCotizacion, ediciones: readonly EdicionCampo[]): void {
  const lecturas = copia.lecturas as Record<string, unknown> | null | undefined;
  for (const M of ['A', 'B'] as const) {
    const viejo = antes.lecturas[M];
    const lectura = lecturas?.[M] as { historial?: unknown } | null | undefined;
    const base = `/lecturas/${M}/historial`;
    const mias = ediciones.filter((e) => e.ruta === base || e.ruta.startsWith(`${base}/`) || e.ruta === `/lecturas/${M}`);
    if (!viejo || !viejo.historial.length || !mias.length || !lectura || !Array.isArray(lectura.historial)) continue;
    const nuevo = lectura.historial as PeriodoConsumo[];
    const porIndice = nuevo.length === viejo.historial.length && mias.every((e) => e.op === 'reemplazar' && e.ruta !== base && e.ruta !== `/lecturas/${M}`);
    // hoy85 (verificador de hoy84): con agregar/quitar renglones, el periodo de antes se reconoce como lo reconoce la regla del
    // importe (periodosDeAntes: fechas, corte, etiqueta). Antes se buscaba por «etiqueta|inicio|fin» y, si en el mismo guardado
    // cambiaban también las fechas o la etiqueta (el alta que se toma como el periodo completo, un renglón sin fechas que se
    // acomoda), el importe viejo quedaba pegado a los kWh nuevos.
    const validos = nuevo.map((n) => (n && typeof n === 'object' && typeof n.consumo_kwh === 'number' && typeof n.etiqueta === 'string' ? n : null));
    const deAntes = porIndice ? null : periodosDeAntes(viejo.historial, validos.map((n) => n ?? ({ etiqueta: '', inicio: null, fin: null, importe: null } as unknown as PeriodoConsumo)), { porIndice: false });
    nuevo.forEach((n, i) => {
      if (!validos[i]) return;
      const j = deAntes ? deAntes[i] : i;
      const p = j === null || j === undefined ? undefined : viejo.historial[j];
      if (!p || n.consumo_kwh === p.consumo_kwh) return;
      const suya = mias.find((e) => e.ruta === `${base}/${i}/consumo_kwh` || e.ruta === `${base}/${i}`) ?? mias.find((e) => e.motivo);
      nuevo[i] = periodoConConsumoEditado(p, n, { motivo: suya?.motivo?.trim() || MOTIVO_EDITADO_A_MANO, fuente: 'vendedor' });
    });
  }
}

/** hoy86 (verificación r2): la marca «lo estimó la IA» (`sugerido_ia`) la cuida el servidor, no depende de quién llama (pantalla, API,
 *  «Otra corrección con IA»):
 *  · si cambian los kWh de un mes marcado, ya no son de la IA: la marca se quita (antes se quedaba y el aviso, el historial y el PDF
 *    decían «por la IA» de un número que la IA nunca dio). Lo único que los cambia con la marca es volver a apretar «Sugerir con IA»,
 *    que va como tabla entera con su motivo (MOTIVO_SUGERIR_IA);
 *  · un mes que dejó de ser «estimado» pierde la marca;
 *  · una marca NUEVA en algo que no es «estimado» (un recibo real que diría «estimado por la IA») no se guarda: truena. */
function cuidarMarcasDeIA(copia: Record<string, unknown>, antes: ContenidoCotizacion, ediciones: readonly EdicionCampo[]): void {
  const lecturas = copia.lecturas as Record<string, unknown> | null | undefined;
  for (const M of ['A', 'B'] as const) {
    const lectura = lecturas?.[M] as { historial?: unknown } | null | undefined;
    const base = `/lecturas/${M}/historial`;
    const mias = ediciones.filter((e) => e.ruta === base || e.ruta.startsWith(`${base}/`) || e.ruta === `/lecturas/${M}`);
    if (!mias.length || !lectura || !Array.isArray(lectura.historial)) continue;
    const nuevo = lectura.historial as PeriodoConsumo[];
    const previa = antes.lecturas[M]?.historial ?? [];
    const porIndice = nuevo.length === previa.length && mias.every((e) => e.op === 'reemplazar' && e.ruta !== base && e.ruta !== `/lecturas/${M}`);
    const validos = nuevo.map((n) => (n && typeof n === 'object' && typeof n.consumo_kwh === 'number' && typeof n.etiqueta === 'string' ? n : null));
    const deAntes = porIndice ? null : periodosDeAntes(previa, validos.map((n) => n ?? ({ etiqueta: '', inicio: null, fin: null, importe: null } as unknown as PeriodoConsumo)), { porIndice: false });
    const volvioASugerir = !porIndice && mias.some((e) => e.motivo?.trim() === MOTIVO_SUGERIR_IA);
    nuevo.forEach((n, i) => {
      if (!validos[i] || !n.sugerido_ia) return;
      const j = deAntes ? deAntes[i] : i;
      const p = j === null || j === undefined ? undefined : previa[j];
      if (n.origen !== 'estimado') {
        if (!p?.sugerido_ia) throw new ErrorCotizador('EDICION_INVALIDA', `«Estimado por la IA» solo va en un mes estimado: ${n.etiqueta} viene del recibo o está escrito a mano.`, [`${base}/${i}/sugerido_ia`]);
        nuevo[i] = { ...n, sugerido_ia: null };
      } else if (p?.sugerido_ia && p.consumo_kwh !== n.consumo_kwh && !volvioASugerir) nuevo[i] = { ...n, sugerido_ia: null };
    });
  }
}

/** Aplica TODAS las ediciones o ninguna. Devuelve el contenido nuevo, validado. */
export function aplicarEdiciones(contenido: ContenidoCotizacion, ediciones: readonly EdicionCampo[]): ContenidoCotizacion {
  if (ediciones.length === 0) throw new ErrorCotizador('EDICION_INVALIDA', 'No hay nada que cambiar.');
  const copia = structuredClone(contenido) as unknown as Record<string, unknown>;
  for (const e of ediciones) aplicarUna(copia, e);
  marcarCoberturasEscritas(copia, contenido, ediciones);
  fijarPanelesDelMejorRetorno(copia, contenido);
  marcarConsumosEditados(copia, contenido, ediciones);
  cuidarMarcasDeIA(copia, contenido, ediciones);
  const r = ContenidoCotizacion.safeParse(copia);
  if (!r.success)
    throw new ErrorCotizador(
      'EDICION_INVALIDA',
      'El cambio deja la cotización con datos que no sirven.',
      r.error.issues.map((i) => `/${i.path.join('/')}: ${i.message}`),
    );
  return r.data;
}
